/**
 * RouteFallback — the spinner shown while a lazy route chunk downloads, with a
 * deadline.
 *
 * The app already recovers from a chunk that FAILS: `vite:preloadError` and the
 * error boundary both route into a reload. Nothing handled a chunk that simply
 * never arrives — no error, no rejection, just a request that hangs. Suspense
 * then holds this fallback forever and the user sits watching a spinner with no
 * way out. That is the "blocked with the loading circle, i cant even close the
 * window" report: on an installed PWA there is no tab to close, so a spinning
 * app is a dead end.
 *
 * So: after a grace period, say what happened and offer the same cache-busting
 * reload the failure path uses. Better a visible escape than an honest-looking
 * spinner that will never resolve.
 */

import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { bustCachesAndReload } from "@/utils/recoverApp"

/** Long enough that a slow 3G chunk still lands normally; short enough that a
 *  hung one does not feel like an eternity. */
const STUCK_AFTER_MS = 10_000

export function RouteFallback() {
  const [stuck, setStuck] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => setStuck(true), STUCK_AFTER_MS)
    return () => clearTimeout(t)
  }, [])

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-[#030303] px-6 text-center">
      {!stuck ? (
        <Loader2 className="h-6 w-6 animate-spin text-[#F59122]" />
      ) : (
        <>
          <p className="font-satoshi text-lg text-white">This is taking too long</p>
          <p className="max-w-[22rem] font-satoshi text-sm leading-relaxed text-[#7A7A7A]">
            Part of the app didn&apos;t load. Reloading clears the cached copy and
            fetches it again.
          </p>
          <button
            type="button"
            onClick={() => void bustCachesAndReload()}
            className="rounded-lg bg-[#F59122] px-7 py-2.5 font-satoshi text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Reload
          </button>
          <p className="max-w-[22rem] font-satoshi text-xs leading-relaxed text-[#7A7A7A]/70">
            If it comes back, fully close the app and reopen it.
          </p>
        </>
      )}
    </div>
  )
}

export default RouteFallback
